import { CreateManualTaskDto, TaskStatus, TaskStatusEnum } from "@dune/types";
import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Input,
	Label,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	TextArea,
} from "@dune/ui";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import {
	useCreateManualTask,
	useGetAssignableUsers,
} from "../../../../../api/queries/tasks.queries";

interface CreateManualTaskModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	brokerId: number;
	onSuccess?: () => void;
}

interface FormData {
	title: string;
	description?: string;
	assigneeId?: number | null;
	status?: TaskStatus;
	dueDate?: string;
}

const CreateManualTaskModal: React.FC<CreateManualTaskModalProps> = ({
	open,
	onOpenChange,
	brokerId,
	onSuccess,
}) => {
	const { data: assignableUsers, isLoading: isLoadingUsers } =
		useGetAssignableUsers();
	const createTaskMutation = useCreateManualTask(brokerId);

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors, isValid },
	} = useForm<FormData>({
		defaultValues: {
			title: "",
			description: "",
			assigneeId: null,
			status: TaskStatusEnum.TO_DO,
			dueDate: "",
		},
	});

	const handleCreate = async (data: FormData) => {
		const createTaskDto: CreateManualTaskDto = {
			title: data.title,
			description: data.description || undefined,
			assigneeId: data.assigneeId || undefined,
			status: data.status || TaskStatusEnum.TO_DO,
			dueDate: data.dueDate ? new Date(data.dueDate) : undefined,
			relatedEntities: [
				{
					entityType: "BROKER",
					entityId: brokerId,
				},
			],
		};

		createTaskMutation.mutate(createTaskDto, {
			onSuccess: () => {
				toast.success("Tâche créée avec succès");
				onOpenChange(false);
				reset();
				if (onSuccess) {
					onSuccess();
				}
			},
			onError: (error: unknown) => {
				const message =
					error instanceof Error
						? error.message
						: "Erreur lors de la création de la tâche";
				toast.error(message);
			},
		});
	};

	const handleClose = () => {
		onOpenChange(false);
		reset();
	};

	const statusOptions = [
		{ value: TaskStatusEnum.TO_DO, label: "À faire" },
		{ value: TaskStatusEnum.IN_PROGRESS, label: "En cours" },
		{ value: TaskStatusEnum.DONE, label: "Terminé" },
	];

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="max-w-lg">
				<DialogHeader>
					<DialogTitle>Créer une nouvelle tâche</DialogTitle>
					<DialogDescription>
						Créez une tâche manuelle pour ce courtier
					</DialogDescription>
				</DialogHeader>

				<form onSubmit={handleSubmit(handleCreate)} className="space-y-4 py-4">
					<div className="space-y-2">
						<Label htmlFor="title">
							Titre <span className="text-red-500">*</span>
						</Label>
						<Controller
							name="title"
							control={control}
							rules={{ required: "Le titre est obligatoire" }}
							render={({ field }) => (
								<Input
									{...field}
									id="title"
									placeholder="Titre de la tâche"
									className={errors.title ? "border-red-500" : ""}
								/>
							)}
						/>
						{errors.title && (
							<p className="text-sm text-red-500">{errors.title.message}</p>
						)}
					</div>

					<div className="space-y-2">
						<Label htmlFor="description">Description</Label>
						<Controller
							name="description"
							control={control}
							render={({ field }) => (
								<TextArea
									{...field}
									id="description"
									placeholder="Description de la tâche (optionnel)"
									rows={5}
								/>
							)}
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="assignee">Assigné à</Label>
						<Controller
							name="assigneeId"
							control={control}
							render={({ field }) => (
								<Select
									value={
										field.value !== null && field.value !== undefined
											? field.value.toString()
											: "none"
									}
									onValueChange={(value) =>
										field.onChange(value === "none" ? null : Number(value))
									}
									disabled={isLoadingUsers}
								>
									<SelectTrigger id="assignee">
										<SelectValue
											placeholder={
												isLoadingUsers
													? "Chargement..."
													: "Sélectionnez un utilisateur"
											}
										/>
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="none">Aucun assigné</SelectItem>
										{assignableUsers?.map((user) => (
											<SelectItem key={user.id} value={user.id.toString()}>
												{user.firstname} {user.lastname}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							)}
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="status">Statut</Label>
						<Controller
							name="status"
							control={control}
							render={({ field }) => (
								<Select
									value={field.value || TaskStatusEnum.TO_DO}
									onValueChange={(value) => field.onChange(value as TaskStatus)}
								>
									<SelectTrigger id="status">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{statusOptions.map((option) => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							)}
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="dueDate">Date d'échéance</Label>
						<Controller
							name="dueDate"
							control={control}
							render={({ field }) => (
								<Input
									{...field}
									id="dueDate"
									type="date"
									min={new Date().toISOString().split("T")[0]}
								/>
							)}
						/>
					</div>

					<DialogFooter>
						<Button type="button" variant="outline" onClick={handleClose}>
							Annuler
						</Button>
						<Button
							type="submit"
							disabled={!isValid || createTaskMutation.isPending}
							isLoading={createTaskMutation.isPending}
						>
							Créer la tâche
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default CreateManualTaskModal;
